import type { Renderer } from '@fluentui/react-northstar-styles-renderer';
import type { IRenderer, IStyle, TEnhancer, TPlugin } from 'fela';
import { createRenderer } from 'fela';
import felaPluginEmbedded from 'fela-plugin-embedded';
import felaPluginFallbackValue from 'fela-plugin-fallback-value';
import felaPluginPlaceholderPrefixer from 'fela-plugin-placeholder-prefixer';
import { RendererProvider } from 'react-fela';

import { felaDisableAnimationsPlugin } from './felaDisableAnimationsPlugin';
import { felaFocusVisibleEnhancer } from './felaFocusVisibleEnhancer';
import { felaInvokeKeyframesPlugin } from './felaInvokeKeyframesPlugin';
import { felaSanitizeCssPlugin } from './felaSanitizeCssPlugin';
import sortMediaQueryMobileFirst from 'fela-sort-media-query-mobile-first';
import type { FelaRendererParam } from './types';
import { render } from 'fela-dom';

let felaDevMode = false;

try {
    // eslint-disable-next-line no-undef
    felaDevMode = !!window.localStorage.felaDevMode;
} catch {}

const blocklistedClassNames = [
    // Blocklist contains a list of classNames that are used by FontAwesome
    // https://fontawesome.com/how-to-use/on-the-web/referencing-icons/basic-use
    'fa',
    'fas',
    'far',
    'fal',
    'fab',
    // Used by https://github.com/fullcalendar/fullcalendar
    'fc',
    // .cke is used by CKEditor
    'ck',
    'cke',
];

const filterClassName = (className: string): boolean => {
    // Also ensure that class name does not contain 'ad' as it might
    // cause compatibility issues regarding Ad blockers.
    return (
        className.indexOf('ad') === -1 &&
        blocklistedClassNames.indexOf(className) === -1
    );
};

// The cascade layer that holds Aria's runtime-injected reset styles
// (normalize.css + globalStyles). It is declared below `utilities` in every
// Tailwind entrypoint (`@layer theme, base, components, aria, utilities;`), so
// Tailwind utility classes always win over these element-level resets while
// preflight/base defaults keep their weight. Layering happens here, at inject
// time, so it holds regardless of how `@archipro-design/aria` is built — the app
// consumes it as a prebuilt bundle, so a source-level `@layer` would not apply.
const ARIA_STATIC_LAYER = 'aria';

// Root-font-sizing statics (Aria's `responsiveRemCSS` / `desktopOnlyCSS` set
// `html { font-size … }`) are intentionally left un-layered: they are not a
// utilities conflict and must keep their cascade weight against BM's legacy
// (un-layered, styled-components) root-font CSS — layering them could shift BM's
// root font size. Everything else Aria injects (normalize.css + globalStyles) is
// routed into the `aria` layer. normalize's own `html` rule sets line-height,
// not font-size, so it is not matched here.
//
// This match is content-coupled to Aria's `responsiveRemCSS` shape. If that
// string is ever reformatted so it stops matching, the renderer would wrap it
// and BM's root font could regress — so `aria-cascade-layer.test.ts` drives the
// real `baseTheme.staticStyles` and asserts the root-font static stays
// un-layered, turning that coupling into a caught test failure, not a silent bug.
const ROOT_FONT_STATIC = /html\s*\{\s*font-size/i;

// Serialise a flat Fela static-style object (e.g. Aria's `globalStyles`, keyed
// by selector) into a CSS declaration string. Aria's static reset objects are
// simple string/number maps with no nesting or fallback arrays (verified in
// `globalStyles.ts`), so the Fela plugins are no-ops for them and a direct
// cssify is faithful. camelCase properties are hyphenated; already-hyphenated
// vendor keys (e.g. `-webkit-tap-highlight-color`) are left as-is;
// undefined/empty values (optional siteVariables) are dropped.
//
// This does NOT support nested objects (media/pseudo) or fallback-value arrays.
// If a future Aria upgrade introduces them, throw loudly here — the regression
// test drives the real `baseTheme.staticStyles`, so it is caught pre-merge —
// rather than silently emitting `[object Object]` into the stylesheet.
const cssifyStaticDeclarations = (style: Record<string, unknown>): string =>
    Object.entries(style)
        .filter(
            ([, value]) =>
                value !== undefined && value !== null && value !== '',
        )
        .map(([property, value]) => {
            if (typeof value === 'object') {
                throw new Error(
                    `Aria static style "${property}" is a nested value; ` +
                        `cssifyStaticDeclarations only supports flat string/` +
                        `number declarations. Extend it to serialise nested ` +
                        `rules before upgrading Aria.`,
                );
            }
            return `${property.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`)}:${value as string}`;
        })
        .join(';');

const rendererConfig = {
    devMode: felaDevMode,
    filterClassName,
    enhancers: [sortMediaQueryMobileFirst()],
    plugins: [
        felaDisableAnimationsPlugin as TPlugin,

        // is necessary to prevent accidental style typos
        // from breaking ALL the styles on the page
        felaSanitizeCssPlugin as TPlugin,

        felaPluginPlaceholderPrefixer(),
        felaInvokeKeyframesPlugin as TPlugin,
        felaPluginEmbedded(),

        // Heads up!
        // This is required after fela-plugin-prefixer to resolve the array of fallback values prefixer produces.
        felaPluginFallbackValue(),
    ],
};

// Base interface is missing types..
interface FelaRenderer extends IRenderer {
    listeners: Function[];
    nodes: Record<string, HTMLStyleElement>;
    updateSubscription: Function | undefined;
}

export interface FluentUIRenderer extends Renderer {
    reset: () => void;
}

export const createFelaRendererFactory: (options?: {
    // Whether Aria's static styles are wrapped in the `aria` cascade layer.
    // Defaults to true for browser apps (web + Business Manager) so Tailwind
    // utilities win. Email rendering (`web-email`) passes false — many email
    // clients don't support `@layer` and would drop the wrapped reset rules.
    layerAriaStatics?: boolean;
}) => {
    create: () => FluentUIRenderer;
    felaRenderer: FelaRenderer;
} = ({ layerAriaStatics = true } = {}) => {
    const felaRenderer = createRenderer(rendererConfig) as FelaRenderer;

    return {
        felaRenderer,
        create: () => {
            let usedRenderers = 0;

            const Provider = (props: any) => (
                <RendererProvider
                    renderer={felaRenderer}
                    rehydrate={typeof document !== 'undefined'}
                    targetDocument={props.target}
                >
                    {props.children}
                </RendererProvider>
            );

            return {
                registerUsage: () => {
                    usedRenderers += 1;
                },
                unregisterUsage: () => {
                    usedRenderers -= 1;

                    if (usedRenderers === 0) {
                        felaRenderer.listeners = [];
                        felaRenderer.nodes = {};
                        felaRenderer.updateSubscription = undefined;
                    }
                },

                renderFont: (font) => {
                    felaRenderer.renderFont(font.name, font.paths, font.props);
                },
                renderGlobal: (style, selector) => {
                    // Aria's static styles are un-layered by default, which beats
                    // every Tailwind `@layer utilities` rule regardless of
                    // specificity. Route them into the `aria` cascade layer so
                    // utilities win automatically. The opt-out (email) keeps the
                    // un-layered passthrough.
                    if (!layerAriaStatics) {
                        felaRenderer.renderStatic(style as string, selector);
                        return;
                    }
                    if (typeof style === 'string') {
                        // String statics: normalize.css (wrap) vs the responsive
                        // root-font sizing (left un-layered — see ROOT_FONT_STATIC).
                        const css = ROOT_FONT_STATIC.test(style)
                            ? style
                            : `@layer ${ARIA_STATIC_LAYER}{${style}}`;
                        felaRenderer.renderStatic(css, selector);
                        return;
                    }
                    // Object statics (e.g. `globalStyles`) arrive keyed by
                    // `selector`; cssify + wrap them per selector.
                    const declarations = cssifyStaticDeclarations(
                        style as Record<string, unknown>,
                    );
                    felaRenderer.renderStatic(
                        `@layer ${ARIA_STATIC_LAYER}{${selector}{${declarations}}}`,
                    );
                },
                renderRule: (styles, param) => {
                    const felaParam: FelaRendererParam = {
                        ...param,
                        theme: { direction: param.direction },
                    };

                    return felaRenderer.renderRule(
                        () => styles as unknown as IStyle,
                        felaParam,
                    );
                },

                // Only used by useStyles in Aria.
                getOriginalRenderer: (): IRenderer => {
                    return felaRenderer;
                },

                reset: (): void => {
                    if (felaRenderer.updateSubscription) {
                        /**
                         * Remove existing subscription
                         * @see {import('fela-dom').render}
                         */
                        felaRenderer.listeners.splice(
                            felaRenderer.listeners.indexOf(
                                felaRenderer.updateSubscription,
                            ),
                            1,
                        );
                        felaRenderer.updateSubscription = undefined;
                    }

                    // Base typings missing the 2nd arg
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    render(felaRenderer, document);
                },

                Provider,
            };
        },
    };
};
